'use client';

import Link from 'next/link'
import { usePathname } from 'next/navigation'

interface BreadcrumbsProps {
  data?: {
    heading?: {
      title?: string
    }
  }
}

export default function Breadcrumbs({ data }: BreadcrumbsProps) {
  const pathname = usePathname()
  
  if (!pathname) return null;

  // Pagination slugs to strip from breadcrumbs along with any trailing number
  const paginationSlugs = new Set(['page'])

  const rawPaths = pathname.split('/').filter(Boolean)

  // Remove pagination segments: drop "page" and the numeric segment after it
  const paths = rawPaths.filter((segment, index) => {
    if (paginationSlugs.has(segment.toLowerCase())) return false
    if (index > 0 && paginationSlugs.has(rawPaths[index - 1]?.toLowerCase()) && /^\d+$/.test(segment)) return false
    return true
  })

  // Path segments that have no dedicated page — show as text, not link
  const unlinkedSegments = ['facelift']

  // Function to format path text
  const formatPathText = (path: string) => {
    const uppercaseAcronyms = new Set(['ai'])

    // Remove any numbers, hyphens, or "-test" suffix from the end of the path
    const cleanPath = path.replace(/-test$/i, '').replace(/-?\d+$/, '').replace(/-+$/, '')
    
    // Split by hyphens and format each word
    const formattedText = cleanPath.split('-')
      .map(word => {
        const lowerWord = word.toLowerCase()
        if (uppercaseAcronyms.has(lowerWord)) {
          return lowerWord.toUpperCase()
        }

        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
      .join(' ')
    
    // Add ® symbol next to Botox
    if (formattedText.toLowerCase().includes('botox')) {
      return formattedText.replace(/botox/gi, 'Botox®')
    }
    
    return formattedText
  }

  return (
    <div className='Breadcrumbs w-full bg-neutral-200 py-6 md:py-12'>
      <div className='container'>
        <nav className='flex flex-wrap items-center gap-2'>
          <Link
            href='/'
            className='text-gray-slate hover:text-primary transition-colors'
          >
            Home
          </Link>
          {paths.map((path, index) => {
            const url = `/${paths.slice(0, index + 1).join('/')}`
            const formattedText = formatPathText(path)
            
            const isLast = index === paths.length - 1
            const isUnlinked = unlinkedSegments.includes(path.toLowerCase())

            return (
              <div key={path} className='flex items-center gap-2'>
                <span className='text-gray-slate'>/</span>
                {isLast || isUnlinked ? (
                  <span className='text-gray-slate'>{formattedText}</span>
                ) : (
                  <Link
                    href={url}
                    className='text-gray-slate hover:text-primary transition-colors'
                  >
                    {formattedText}
                  </Link>
                )}
              </div>
            )
          })}
        </nav>
      </div>
    </div>
  )
}